import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { LuHeart, LuQuote, LuStar, LuTrophy, LuVideo } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { storeReviewUrl } from '../../config/app-store';
import { Button } from '../../design-system/components';
import { fireAndForget } from '../../utils/fireAndForget';
import { getJourneyMemory } from '../../utils/journey-memory';
import CelebrationPanel from './CelebrationPanel';

interface JourneyThrowbackPanelProps {
  onClose: () => void;
}

export default function JourneyThrowbackPanel({ onClose }: JourneyThrowbackPanelProps) {
  const navigate = useNavigate();
  const journeyMemory = getJourneyMemory();

  // Explicit "Rate" button: deep-link to the store's review surface so a tap
  // always does something. requestReview() is intentionally NOT used here — it's
  // throttled and frequently shows nothing, which makes an explicit button feel
  // broken. (System-initiated prompts use requestReview elsewhere, via the
  // review-prompt util.) iOS pre-launch has no store URL yet, so it falls back
  // to the in-app API until IOS_APP_STORE_ID is set.
  const handleReview = async () => {
    if (!Capacitor.isNativePlatform()) {
      toast.success('Thank you for your support!');
      return;
    }

    const url = storeReviewUrl(Capacitor.getPlatform());
    try {
      if (url) {
        await Browser.open({ url });
      } else {
        // iOS pre-launch: no store URL yet. requestReview() is throttled and may
        // show nothing without throwing, so acknowledge unconditionally rather
        // than relying on catch — the tap must never feel like a no-op.
        const { InAppReview } = await import('@capacitor-community/in-app-review');
        await InAppReview.requestReview();
        toast.success('Thank you for your support!');
      }
    } catch {
      toast.success('Thank you for your support!');
    }
  };

  const handleTryEmpathy = () => {
    onClose();
    void navigate('/app/empathy');
  };

  return (
    <CelebrationPanel
      onClose={onClose}
      avatarBadge={
        <span
          aria-hidden="true"
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[var(--ee-gold)] text-[var(--ee-navy)] flex items-center justify-center ring-2 ring-[var(--ee-surface)] shadow-md"
        >
          <LuTrophy className="w-3.5 h-3.5" />
        </span>
      }
      title="Look How Far You've Come!"
      subtitle="Course Complete"
      actions={
        <>
          <Button
            variant="primary"
            fullWidth
            leadingIcon={<LuStar />}
            onClick={fireAndForget(handleReview)}
          >
            Rate EmpathEase
          </Button>
          <Button
            variant="secondary"
            fullWidth
            leadingIcon={<LuVideo />}
            onClick={handleTryEmpathy}
          >
            On-Demand Empathy
          </Button>
        </>
      }
    >
      {journeyMemory?.toolInputText ? (
        <div className="mb-5">
          <p className="text-[var(--ee-text-secondary)] text-xs font-semibold uppercase tracking-wider mb-2.5 text-center">
            Where it all started
          </p>
          <blockquote className="relative bg-[var(--ee-surface-warm)] rounded-[var(--ee-radius-md)] px-4 py-3.5 mb-4">
            <LuQuote
              aria-hidden="true"
              className="absolute -top-2 left-4 w-5 h-5 text-[var(--ee-orange)] opacity-40"
            />
            <p className="text-sm text-[var(--ee-navy)] italic leading-relaxed pl-2">
              {journeyMemory.toolInputText}
            </p>
          </blockquote>
          <p className="text-[var(--ee-text-secondary)] text-sm leading-relaxed text-center">
            Your goal was to{' '}
            <span className="font-semibold text-[var(--ee-orange-text)]">
              {journeyMemory.goalLabel.toLowerCase()}
            </span>
            . You&apos;ve completed the entire NVC course — what an incredible journey!
          </p>
        </div>
      ) : journeyMemory?.goalLabel ? (
        <div className="mb-5">
          <p className="text-[var(--ee-text-secondary)] text-sm leading-relaxed text-center">
            You started this journey wanting to{' '}
            <span className="font-semibold text-[var(--ee-orange-text)]">
              {journeyMemory.goalLabel.toLowerCase()}
            </span>
            . Now you&apos;ve completed the entire NVC course — what an incredible achievement!
          </p>
        </div>
      ) : (
        <p className="text-[var(--ee-text-secondary)] text-sm leading-relaxed mb-5 text-center">
          You&apos;ve completed the entire NVC course — what an incredible achievement! Your
          dedication to empathic communication is truly inspiring.
        </p>
      )}

      {/* Decorative divider */}
      <div aria-hidden="true" className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-[var(--ee-border-strong)]"></div>
        <LuHeart className="w-3 h-3 text-[var(--ee-orange)] opacity-50" />
        <div className="flex-1 h-px bg-[var(--ee-border-strong)]"></div>
      </div>

      <p className="text-[var(--ee-text-secondary)] text-xs leading-relaxed text-center">
        If EmpathEase has helped you, consider leaving a review — it helps others discover empathic
        communication too.
      </p>
    </CelebrationPanel>
  );
}
